import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { AvatarImage } from "@/components/ui/AvatarImage";
import { Button } from "@/components/ui/Button";
import { LoadingIndicator } from "@/components/ui/LoadingIndicator";
import { useAuth } from "@/context/AuthContext";
import {
  arrayUnion,
  deleteDoc,
  doc,
  collection as firestoreCollection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { useSenderPhotoURL } from "@/hooks/useSenderPhotoURL";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useFormatTimeAgo } from "@/utils/formatDate";
import { DisplayName } from "@/components/ui/DisplayName";
import { router } from "expo-router";
import Head from "expo-router/head";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { GestureResponderEvent } from "react-native";
import { FlatList, StyleSheet, TouchableOpacity } from "react-native";

const firestore = getFirestore(getApp());
const TAB_BAR_HEIGHT = 40;

type Notification = {
  id: string;
  type:
    | "LIKE"
    | "COMMENT"
    | "COMMENT_ON_FOLLOWED_POST"
    | "FOLLOW"
    | "NEW_POST"
    | "GROUP_INVITATION"
    | "GROUP_POST"
    | "GROUP_ACCESS_REQUEST"
    | "GROUP_ACCESS_APPROVED";
  sender: {
    uid: string;
    displayName?: string;
    photoURL?: string;
  };
  resourceId: string;
  groupId?: string;
  groupName?: string;
  createdAt: Timestamp;
  read: boolean;
  // GROUP_INVITATION only — set when the invitee acts from either the
  // notifications tab or the Groups tab. When present the row renders the
  // ACCEPTED / DECLINED label and hides the buttons.
  interactionStatus?: "accepted" | "declined";
};

const NotificationItem = memo(function NotificationItem({
  uid,
  item,
  isHighlighted,
  clearHighlight,
}: {
  uid?: string;
  item: Notification;
  isHighlighted: boolean;
  clearHighlight: (id: string) => void;
}) {
  const { formatTimeAgo } = useFormatTimeAgo();
  const backgroundColor = useThemeColor(
    {},
    isHighlighted ? "notificationBackground" : "background"
  );
  const textColor = useThemeColor({}, "text");
  const { t } = useTranslation();
  const resolvedPhotoURL = useSenderPhotoURL(item.sender?.uid);
  // Only GROUP_ACCESS_REQUEST rows read status from a separate doc.
  // GROUP_INVITATION rows now render from `item.interactionStatus` written
  // directly onto the notification doc — no per-row invitation subscription.
  const [requestStatus, setRequestStatus] = useState<
    "pending" | "approved" | "declined" | "loading" | "unknown"
  >(item.type === "GROUP_ACCESS_REQUEST" ? "loading" : "unknown");
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    if (item.type !== "GROUP_ACCESS_REQUEST" || !item.resourceId) return;
    const unsubscribe = onSnapshot(
      doc(firestore, "groupAccessRequests", item.resourceId),
      (snap) => {
        if (!snap.exists()) {
          setRequestStatus("unknown");
          return;
        }
        const status = snap.data().status as string;
        if (status === "pending") setRequestStatus("pending");
        else if (status === "approved") setRequestStatus("approved");
        else if (status === "declined") setRequestStatus("declined");
        else setRequestStatus("unknown");
      },
      () => {
        setRequestStatus("unknown");
      },
    );
    return () => unsubscribe();
  }, [item.resourceId, item.type]);

  const markRead = async () => {
    if (!uid || item.read) return;
    try {
      await updateDoc(
        doc(firestore, "users", uid, "notifications", item.id),
        { read: true },
      );
    } catch {
      // best-effort
    }
  };

  const handleApprove = async () => {
    if (!item.groupId || !item.resourceId || !item.sender?.uid) return;
    clearHighlight(item.id);
    setActionPending(true);
    try {
      const batch = writeBatch(firestore);
      batch.update(doc(firestore, "groups", item.groupId), {
        memberIds: arrayUnion(item.sender.uid),
      });
      batch.update(doc(firestore, "groupAccessRequests", item.resourceId), {
        status: "approved",
      });
      await batch.commit();
      setRequestStatus("approved");
      await markRead();
    } catch (error) {
      console.error("Error approving from notification:", error);
    } finally {
      setActionPending(false);
    }
  };

  const handleDecline = async () => {
    if (!item.resourceId) return;
    clearHighlight(item.id);
    setActionPending(true);
    try {
      await updateDoc(
        doc(firestore, "groupAccessRequests", item.resourceId),
        { status: "declined" },
      );
      setRequestStatus("declined");
      await markRead();
    } catch (error) {
      console.error("Error declining from notification:", error);
    } finally {
      setActionPending(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!uid || !item.resourceId) return;
    const groupId = item.groupId;
    if (!groupId) return;
    clearHighlight(item.id);
    setActionPending(true);
    try {
      try {
        const batch = writeBatch(firestore);
        batch.update(doc(firestore, "users", uid), {
          groupIds: arrayUnion(groupId),
        });
        batch.update(doc(firestore, "groups", groupId), {
          memberIds: arrayUnion(uid),
        });
        batch.delete(doc(firestore, "groupInvitations", item.resourceId));
        await batch.commit();
      } catch (batchError) {
        // Legacy notifications whose invitation was already resolved via the
        // old status-flip flow — the delete rule requires status == "pending",
        // so a batch on a resolved/missing doc fails. Record the user's
        // interaction on the notification anyway so the row reflects the
        // choice. Genuine failures (auth, network) leave the notification
        // update to fail below, keeping state consistent.
        console.warn(
          "Invitation batch failed (likely already resolved):",
          batchError,
        );
      }
      await updateDoc(
        doc(firestore, "users", uid, "notifications", item.id),
        { read: true, interactionStatus: "accepted" },
      );
    } catch (error) {
      console.error("Error accepting invitation from notification:", error);
    } finally {
      setActionPending(false);
    }
  };

  const handleDeclineInvitation = async () => {
    if (!uid || !item.resourceId) return;
    clearHighlight(item.id);
    setActionPending(true);
    try {
      try {
        await deleteDoc(
          doc(firestore, "groupInvitations", item.resourceId),
        );
      } catch (deleteError) {
        // Same legacy handling as accept — the invitation may already be gone
        // or resolved. Still record the interaction on the notification.
        console.warn(
          "Invitation delete failed (likely already resolved):",
          deleteError,
        );
      }
      await updateDoc(
        doc(firestore, "users", uid, "notifications", item.id),
        { read: true, interactionStatus: "declined" },
      );
    } catch (error) {
      console.error("Error declining invitation from notification:", error);
    } finally {
      setActionPending(false);
    }
  };

  const goToSenderProfile = (e?: GestureResponderEvent) => {
    e?.stopPropagation?.();
    if (!item.sender?.uid) return;
    router.push({
      pathname: "/(tabs)/public-profile",
      params: { userId: item.sender.uid },
    });
  };

  const handlePress = () => {
    if (!uid) return;
    clearHighlight(item.id);
    void markRead();

    if (
      item.type === "COMMENT" ||
      item.type === "COMMENT_ON_FOLLOWED_POST" ||
      item.type === "LIKE" ||
      item.type === "NEW_POST" ||
      item.type === "GROUP_POST"
    ) {
      router.push({
        pathname: "/(tabs)/post/[postId]",
        params: { postId: item.resourceId },
      });
    } else if (item.type === "GROUP_INVITATION") {
      router.push({ pathname: "/(tabs)/groups" });
    } else if (item.type === "GROUP_ACCESS_REQUEST") {
      if (item.groupId) {
        router.push({
          pathname: "/(tabs)/groups/group-info",
          params: { groupId: item.groupId },
        });
      } else {
        router.push({ pathname: "/(tabs)/groups" });
      }
    } else if (item.type === "GROUP_ACCESS_APPROVED") {
      if (item.groupId) {
        router.push({
          pathname: "/(tabs)/groups/[id]",
          params: { id: item.groupId },
        });
      } else {
        router.push({ pathname: "/(tabs)/groups" });
      }
    }
  };

  const timestampLabel = useMemo(
    () => formatTimeAgo(item.createdAt.toDate().toISOString()),
    [item.createdAt, formatTimeAgo],
  );

  const notificationText = () => {
    switch (item.type) {
      case "LIKE":
        return t("LIKED_YOUR_POST");
      case "COMMENT":
        return t("COMMENTED_ON_YOUR_POST");
      case "COMMENT_ON_FOLLOWED_POST":
        return t("COMMENTED_ON_POST_YOU_COMMENTED_ON");
      case "FOLLOW":
        return t("STARTED_FOLLOWING_YOU");
      case "NEW_POST":
        return t("CREATED_A_NEW_POST");
      case "GROUP_INVITATION": {
        const groupNameDisplay = item.groupName || "a group";
        // Split the translated string around the {{groupName}} placeholder so
        // the group name can render inside a bold ThemedText. Using __GROUPNAME__ as
        // a sentinel keeps this locale-agnostic (works for both "invited you
        // to X." and "te invitó a X.").
        const raw = t("INVITED_YOU_TO_GROUP", { groupName: "__GROUPNAME__" });
        const parts = raw.split("__GROUPNAME__");
        if (parts.length === 2) {
          return (
            <>
              {parts[0]}
              <ThemedText type="defaultSemiBold" style={{ color: textColor }}>
                {groupNameDisplay}
              </ThemedText>
              {parts[1]}
            </>
          );
        }
        return t("INVITED_YOU_TO_GROUP", { groupName: groupNameDisplay });
      }
      case "GROUP_POST":
        return t("POSTED_IN_GROUP", { groupName: item.groupName ?? "" });
      case "GROUP_ACCESS_REQUEST":
        return t("REQUESTED_GROUP_ACCESS", { groupName: item.groupName ?? "" });
      case "GROUP_ACCESS_APPROVED":
        return t("GROUP_ACCESS_APPROVED_BODY", {
          groupName: item.groupName ?? "",
        });
      default:
        return t("PUBLISHED_A_NOTIFICATION");
    }
  };

  return (
    <TouchableOpacity onPress={handlePress}>
      <ThemedView style={[styles.notificationContainer, { backgroundColor }]}>
        <AvatarImage
          photoURL={resolvedPhotoURL}
          alt={item.sender?.displayName ?? "Avatar"}
          style={styles.avatar}
          userId={item.sender?.uid}
        />
        <ThemedView style={styles.textContainer}>
          <ThemedText>
            <ThemedText
              onPress={goToSenderProfile}
              type="defaultSemiBold"
              style={{ color: textColor }}
            >
              <DisplayName
                user={item.sender}
                type="defaultSemiBold"
                style={{ color: textColor }}
              />
            </ThemedText>{" "}
            <ThemedText style={{ color: textColor }}>
              {notificationText()}
            </ThemedText>
          </ThemedText>
          <ThemedText style={[styles.timestamp, { color: textColor }]}>
            {timestampLabel}
          </ThemedText>
          {item.type === "GROUP_ACCESS_REQUEST" &&
            requestStatus === "pending" && (
              <ThemedView style={styles.actions}>
                <Button
                  text={t("APPROVE")}
                  onPress={handleApprove}
                  disabled={actionPending}
                  style={styles.approveButton}
                  textStyle={styles.actionButtonText}
                />
                <Button
                  text={t("DECLINE")}
                  onPress={handleDecline}
                  disabled={actionPending}
                  style={styles.declineButton}
                  textStyle={styles.actionButtonText}
                />
              </ThemedView>
            )}
          {item.type === "GROUP_ACCESS_REQUEST" &&
            requestStatus === "approved" && (
              <ThemedText style={[styles.statusLabel, { color: "#16a34a" }]}>
                {t("APPROVED")}
              </ThemedText>
            )}
          {item.type === "GROUP_ACCESS_REQUEST" &&
            requestStatus === "declined" && (
              <ThemedText style={[styles.statusLabel, { color: "#9ca3af" }]}>
                {t("DECLINED")}
              </ThemedText>
            )}
          {item.type === "GROUP_INVITATION" &&
            !item.interactionStatus && (
              <ThemedView style={styles.actions}>
                <Button
                  text={t("ACCEPT")}
                  onPress={handleAcceptInvitation}
                  disabled={actionPending}
                  style={styles.approveButton}
                  textStyle={styles.actionButtonText}
                />
                <Button
                  text={t("DECLINE")}
                  onPress={handleDeclineInvitation}
                  disabled={actionPending}
                  style={styles.declineButton}
                  textStyle={styles.actionButtonText}
                />
              </ThemedView>
            )}
          {item.type === "GROUP_INVITATION" &&
            item.interactionStatus === "accepted" && (
              <ThemedText style={[styles.statusLabel, { color: "#16a34a" }]}>
                {t("ACCEPTED")}
              </ThemedText>
            )}
          {item.type === "GROUP_INVITATION" &&
            item.interactionStatus === "declined" && (
              <ThemedText style={[styles.statusLabel, { color: "#9ca3af" }]}>
                {t("DECLINED")}
              </ThemedText>
            )}
        </ThemedView>
      </ThemedView>
    </TouchableOpacity>
  );
});

export default function NotificationsScreen() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const wasUnreadIdsRef = useRef<Set<string> | null>(null);
  const [visitUnreadIds, setVisitUnreadIds] = useState<Set<string>>(
    () => new Set()
  );
  const clearHighlight = useCallback((id: string) => {
    setVisitUnreadIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const { t } = useTranslation();

  useEffect(() => {
    if (!user) return;

    const notificationsCollection = firestoreCollection(
      firestore,
      "users",
      user.uid,
      "notifications"
    );
    const q = query(notificationsCollection, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Notification[];
        setNotifications(notifs);
        setLoading(false);
      },
      (err) => {
        console.error("[notifications] snapshot error", err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid || wasUnreadIdsRef.current !== null) return;
    if (notifications.length === 0) return;
    const unread = notifications.filter((n) => !n.read).map((n) => n.id);
    wasUnreadIdsRef.current = new Set(unread);
    if (unread.length === 0) return;
    setVisitUnreadIds(new Set(unread));
    // Firestore batches cap at 500 writes; not a concern at current scale.
    const batch = writeBatch(firestore);
    unread.forEach((id) =>
      batch.update(
        doc(firestore, "users", uid, "notifications", id),
        { read: true },
      ),
    );
    batch.commit().catch(() => {
      /* best-effort — matches per-row markRead */
    });
  }, [notifications, user?.uid]);

  const renderItem = useCallback(
    ({ item }: { item: Notification }) => (
      <NotificationItem
        uid={user?.uid}
        item={item}
        isHighlighted={visitUnreadIds.has(item.id)}
        clearHighlight={clearHighlight}
      />
    ),
    [user?.uid, visitUnreadIds, clearHighlight],
  );

  if (loading) {
    return (
      <ThemedView style={styles.container}>
        <Head>
          <title>{t("PAGE_TITLE_NOTIFICATIONS")}</title>
        </Head>
        <LoadingIndicator />
      </ThemedView>
    );
  }

  return (
    <ThemedView style={styles.container}>
      <Head>
        <title>{t("PAGE_TITLE_NOTIFICATIONS")}</title>
      </Head>
      {notifications.length > 0 ? (
        <FlatList
          data={notifications}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          extraData={visitUnreadIds}
          contentContainerStyle={styles.listContent}
        />
      ) : (
        <ThemedText style={styles.noNotificationsText}>
          {t("NO_NOTIFICATIONS")}
        </ThemedText>
      )}
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
  },
  notificationContainer: {
    flexDirection: "row",
    padding: 15,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 15,
  },
  textContainer: {
    flex: 1,
    padding: 5,
  },
  timestamp: {
    fontSize: 12,
    marginTop: 4,
  },
  noNotificationsText: {
    textAlign: "center",
  },
  listContent: {
    paddingBottom: TAB_BAR_HEIGHT,
  },
  actions: {
    flexDirection: "row",
    marginTop: 8,
    gap: 8,
  },
  approveButton: {
    width: "auto",
    paddingHorizontal: 14,
    paddingVertical: 6,
    backgroundColor: "#16a34a",
  },
  declineButton: {
    width: "auto",
    paddingHorizontal: 14,
    paddingVertical: 6,
    backgroundColor: "#9ca3af",
  },
  actionButtonText: {
    fontSize: 13,
  },
  statusLabel: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: "600",
  },
});
